import React, { useEffect, useRef, useState } from "react";

import BoxComponent from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import useBoxTokenProvider from "hooks/use-box-token-provider.hook";
import { useBoxAssets } from "utils/box-assets.util";

interface BoxFolderPickerProps {
  token: string;
  folderId: string;
  setSelectedFolder: (folder: FolderObject | null) => void;
  onClose: Function;
}

const BoxFolderPicker: React.FC<BoxFolderPickerProps> = React.memo(
  ({ token, folderId, setSelectedFolder, onClose }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { isLoaded, isLoading, error } = useBoxAssets();
    const boxTokenProvider = useBoxTokenProvider(token);
    const folderPicker = useRef<any>(null);
    const chooseHandledRef = useRef(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);

    // Keep the latest callbacks in refs so the Box event listeners and the
    // force-enabled Choose button handler always invoke the current logic.
    // Registering them directly would tear the listeners down (and never
    // re-add them, because the picker already exists) whenever the callback
    // identities change, leaving only the root-folder fallback active.
    const setSelectedFolderRef = useRef(setSelectedFolder);
    const onCloseRef = useRef(onClose);
    setSelectedFolderRef.current = setSelectedFolder;
    onCloseRef.current = onClose;

    // Resolve the Box access token to a string before showing the picker
    useEffect(() => {
      let active = true;
      boxTokenProvider()
        .then((resolvedToken) => {
          if (active) setAccessToken(resolvedToken);
        })
        .catch(() => {
          if (active && token) setAccessToken(token);
        });
      return () => {
        active = false;
      };
    }, [boxTokenProvider, token]);

    // Initialize FolderPicker when Box assets are loaded
    useEffect(() => {
      if (!isLoaded || !window.Box || folderPicker.current) {
        return;
      }

      const { FolderPicker } = window.Box;
      folderPicker.current = new FolderPicker();

      const picker = folderPicker.current;

      const handleChoose = (files: FolderObject[] | null) => {
        chooseHandledRef.current = true;
        setSelectedFolderRef.current(files ? files[0] : null);
        onCloseRef.current();
      };

      const handleCancel = () => onCloseRef.current();

      picker?.addListener("choose", handleChoose);
      picker?.addListener("cancel", handleCancel);

      return () => {
        picker?.removeListener("choose", handleChoose);
        picker?.removeListener("cancel", handleCancel);
      };
    }, [isLoaded]);

    useEffect(() => {
      let fallbackButton: Element | null = null;
      let handleFallbackClick: (() => void) | null = null;

      const loadPicker = () => {
        if (!folderPicker.current || !containerRef.current || !accessToken) {
          return;
        }

        chooseHandledRef.current = false;

        boxTokenProvider().then((resolvedToken) => {
          folderPicker.current?.show(folderId, resolvedToken, {
            container: containerRef.current,
            maxSelectable: 1,
            canCreateNewFolder: true,
            can_upload: true,
            chooseButtonLabel: "Choose Folder",
            canSetShareAccess: false
          });
        });

        setTimeout(() => {
          const button = document.querySelector(".btn-primary");
          if (button) {
            button.removeAttribute("disabled");
            button.classList.remove("is-disabled");
            button.classList.remove("bdl-is-disabled");
            handleFallbackClick = () => {
              setTimeout(() => {
                if (!chooseHandledRef.current) {
                  setSelectedFolderRef.current(null);
                  onCloseRef.current();
                }
              }, 0);
            };
            fallbackButton = button;
            button.addEventListener("click", handleFallbackClick);
          }
        }, 1000);
      };

      if (isLoaded && folderPicker.current) {
        loadPicker();
      }

      return () => {
        if (fallbackButton && handleFallbackClick) {
          fallbackButton.removeEventListener("click", handleFallbackClick);
        }
      };
    }, [folderId, accessToken, isLoaded]);

    useEffect(() => {
      const picker = folderPicker.current;

      return () => {
        picker?.hide();
      };
    }, []);

    // Show loading state while Box assets are loading
    if (isLoading) {
      return (
        <BoxComponent
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={600}
        >
          <CircularProgress />
        </BoxComponent>
      );
    }

    // Show error state if Box assets failed to load
    if (error) {
      return (
        <BoxComponent
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={600}
        >
          Error loading Box components: {error.message}
        </BoxComponent>
      );
    }

    return (
      <BoxComponent
        ref={containerRef}
        height={600}
        className="dtw-folder-container"
        sx={{
          ".bcp-item-row .radio-container": { margin: "0 0 0 3px" },
          ".be-sub-header-right": { display: "none" }
        }}
      />
    );
  }
);

export default BoxFolderPicker;

export interface FolderObject {
  type: string;
  id: string;
  etag: string;
  name: string;
  size: number;
  parent: {
    type: string;
    id: string;
    sequence_id: string;
    etag: string;
    name: string;
  };
  permissions: {
    can_download: boolean;
    can_upload: boolean;
    can_rename: boolean;
    can_delete: boolean;
    can_share: boolean;
    can_invite_collaborator: boolean;
    can_set_share_access: boolean;
  };
  path_collection: {
    total_count: number;
    entries: {
      type: string;
      id: string;
      sequence_id: string;
      etag: string;
      name: string;
    }[];
  };
  modified_at: string;
  created_at: string;
  modified_by: {
    type: string;
    id: string;
    name: string;
    login: string;
  };
  has_collaborations: boolean;
  is_externally_owned: boolean;
  allowed_shared_link_access_levels: string[];
  shared_link: {
    url: string;
    download_url: string | null;
    vanity_url: string | null;
    vanity_name: string | null;
    effective_access: string;
    effective_permission: string;
    is_password_enabled: boolean;
    unshared_at: string | null;
    download_count: number;
    preview_count: number;
    access: string;
    permissions: {
      can_preview: boolean;
      can_download: boolean;
      can_edit: boolean;
    };
  };
}
